import { AABB2 } from "@base/math/AABB2";
import { IVec2, Vec2 } from "@base/math/Vec2";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { PartialRecord } from "@base/util/TypeUtil";
import { EdgeName } from "@biz/definitions/SunSailDefinitions";
import { getEdgeName, getEdgePoleIds } from "@biz/definitions/SunSailDefinitions.util";
import { getNetEdgeIndices, NetShapeType } from "@biz/items/net/NetDefinitions";
import { INetCreationEdgeInfo } from "@biz/items/net/NetVisuLogic";
import { TriangleHelper } from "@biz/items/sunSail/logic/TriangleHelper";

type INetGeometryResult<T> = {
  points: T[];
  error?: undefined;
} | {
  points?: undefined;
  error: "notATriangle" | "emptyInput";
}

interface IBorderPoint {
  out: IVec3;
  in: IVec3;
  p1: IVec3;
  p2: IVec3;
}
export class NetGeometryHelper {
  static #calculateCornerPoints(sides: PartialRecord<EdgeName, number>, shape: NetShapeType): INetGeometryResult<IVec2> {
    if (!sides) return { error: "emptyInput" };

    switch (shape) {
      case "triangle": {
        const A: IVec2 = { x: 0, y: 0 };
        const C: IVec2 = { x: sides.ac!, y: 0 }

        const B = TriangleHelper.calcHeightCPointForDistances2(A, C, sides.ab!, sides.bc!);
        if (!B)
          return { error: "notATriangle" };

        return { points: [A, B, C] };
      }
      case "quad": {
        const A: IVec2 = { x: 0, y: 0 };
        const D: IVec2 = { x: sides.ad!, y: 0 }

        const C = TriangleHelper.calcHeightCPointForDistances2(A, D, sides.ac!, sides.cd!);
        if (!C)
          return { error: "notATriangle" };

        const B = TriangleHelper.calcHeightCPointForDistances2(A, C, sides.ab!, sides.bc!);
        if (!B)
          return { error: "notATriangle" };

        return { points: [A, B, C, D] };
      }

      // throw new Error("Todo");
      case "rectangle": {
        const ab = sides.ab!;
        const bc = sides.bc!;

        const cornerPoints: IVec2[] = [
          { x: 0, y: bc },
          { x: ab, y: bc },
          { x: ab, y: 0 },
          { x: 0, y: 0 },
        ]
        return { points: cornerPoints }
      }
    }
  }

  static calculateCornerPoints2d(sides: PartialRecord<EdgeName, number>, shape: NetShapeType): INetGeometryResult<IVec2> {
    const ret = this.#calculateCornerPoints(sides, shape)
    if (ret.points) {
      const aabb2 = AABB2.create();
      AABB2.growVec2s(aabb2, ret.points);
      const center = AABB2.getCenter(aabb2);
      for (let i = 0; i < ret.points.length; i++) {
        ret.points[i] = Vec2.addElements(ret.points[i], -center.x, 0/*-center.y*/);
      }
    }
    return ret;
  }

  static calculateCornerPoints3d(sides: PartialRecord<EdgeName, number>, shape: NetShapeType): INetGeometryResult<IVec3> {
    const ret = this.calculateCornerPoints2d(sides, shape)
    if (ret.points) {
      const points3d = ret.points.map(p => Vec3.from(p.x, p.y, 0));
      return { points: points3d };
    }
    return { error: ret.error };
  }

  static calcEdgeVectors(points: IVec3[]): PartialRecord<EdgeName, IVec3> {
    const outerEdges: PartialRecord<EdgeName, IVec3> = {};
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const vec = Vec3.to(points[i], points[j]);
        outerEdges[getEdgeName(i, j, false)] = vec;
        outerEdges[getEdgeName(j, i, false)] = Vec3.negate(vec);
      }
    }
    return outerEdges;
  }

  static calcEyeletEdgePoints(outerPoints: IVec3[], edgeName: EdgeName, borderSize: number): IBorderPoint[] {

    const [fromIndex, toIndex] = getNetEdgeIndices(edgeName);
    // if (fromIndex > toIndex) throw new Error("calcEyeletEdgePoints: not implemented so far")
    let indices: number[];
    if (fromIndex < toIndex) {
      const dir = (toIndex - fromIndex === 1 ? 1 : -1)
      indices = [
        (fromIndex + outerPoints.length - dir) % outerPoints.length,
        fromIndex,
        toIndex,
        (toIndex + dir) % outerPoints.length
      ]
    } else {
      const dir = (toIndex - fromIndex === -1 ? -1 : 1);
      indices = [
        (fromIndex + outerPoints.length - dir) % outerPoints.length,
        fromIndex,
        toIndex,
        (toIndex + dir) % outerPoints.length
      ]
    }

    const [p, a, b, n] = indices.map(i => outerPoints[i]);

    return [
      NetGeometryHelper.calcBorderPoint(a, Vec3.to(a, p), Vec3.to(a, b), borderSize, borderSize),
      NetGeometryHelper.calcBorderPoint(b, Vec3.to(b, n), Vec3.to(b, a), borderSize, borderSize),
    ]
  }

  static calcBorderVector(side: IVec3, anotherSide: IVec3, anotherBorderSize: number): IVec3 {
    const normal = Vec3.from(-anotherSide.y, anotherSide.x, 0);
    const nac = Vec3.mul(Vec3.normalized(normal), anotherBorderSize)
    const f = anotherBorderSize / Vec3.len(Vec3.project(side, nac));
    const ab2 = Vec3.mul(side, f);
    return ab2;
  }

  static calcBorderPoints(outerPoints: IVec3[], edgeInfos: Record<EdgeName, INetCreationEdgeInfo>): IBorderPoint[] {
    const outerEdges = NetGeometryHelper.calcEdgeVectors(outerPoints);

    const borderPoints: IBorderPoint[] = [];
    const pointCount = outerPoints.length;
    for (let i = 0; i < pointCount; i++) {
      const borderSizeLast = edgeInfos[getEdgeName((i + pointCount - 1) % pointCount, i)].width;
      const borderSize = edgeInfos[getEdgeName(i, (i + 1) % pointCount)].width;
      borderPoints[i] = NetGeometryHelper.calcBorderPoint(outerPoints[i], outerEdges[getEdgeName(i, (i + 1) % pointCount, false)]!, outerEdges[getEdgeName(i, (i + pointCount - 1) % pointCount, false)]!, borderSizeLast, borderSize);
    }
    return borderPoints;
  }

  static calcBorderPoint(cornerPoint: IVec3, side1: IVec3, side2: IVec3, borderSize1: number, borderSize2?: number): IBorderPoint {
    const v1 = borderSize1 > 0.001 ? NetGeometryHelper.calcBorderVector(side1, side2, borderSize1) : Vec3.ZERO;
    borderSize2 = borderSize2 === undefined ? borderSize1 : borderSize2
    const v2 = borderSize2 > 0.001 ? NetGeometryHelper.calcBorderVector(side2, side1, borderSize2) : Vec3.ZERO;
    return {
      out: cornerPoint,
      in: Vec3.addArray([cornerPoint, v1, v2]),
      p1: Vec3.add(cornerPoint, v1),
      p2: Vec3.add(cornerPoint, v2)
    }
  }

  static getEdgePosition(mountPositions: IVec3[], en: EdgeName, options?: { weight?: number; offsetSide?: number }): IVec3 {
    const { weight = 0.5, offsetSide = 0 } = options || {};
    const poleIndices = getEdgePoleIds(en)
    const ia = poleIndices[0];
    const a = mountPositions[ia]
    const ib = poleIndices[1];
    const b = mountPositions[ib];
    const ret = Vec3.lerp(a, b, weight);
    if (offsetSide) {
      const d = Vec3.dir(a, b);
      return Vec3.addScaled(ret, Vec3.from(-d.y, d.x, d.z), offsetSide);
    }
    return ret;
  }
}
